import { useCallback, useState } from 'react';

import { CLASS_REP_GB_DISCOUNT_PERCENT } from 'my-core/class-rep-course-utils';
import { REFERRAL_DISCOUNT_AMOUNT } from 'my-core/constants';
import { calculateDiscount, getDiscountCodeCheckoutLabel } from 'my-core/discount-code-utils';
import { getPackagePricing, ONLINE_COURSE_PACKAGE_TYPES } from 'my-core/online-course-utils';
import { getSubscriptionProductDiscountPercent, useCurrentUserSubscription } from 'my-core/subscription-utils';

import DialogResponsive from 'my-elements/DialogResponsive';

import OnlineCoursePurchaseFormConfirmation from './OnlineCoursePurchaseFormConfirmation';
import OnlineCoursePurchaseFormHeader from './OnlineCoursePurchaseFormHeader';
import OnlineCoursePurchaseFormInformation from './OnlineCoursePurchaseFormInformation';
import OnlineCoursePurchaseFormPayment from './OnlineCoursePurchaseFormPayment';

import { dialogClasses } from '@mui/material/Dialog';
import { dialogActionsClasses } from '@mui/material/DialogActions';
import { dialogContentClasses } from '@mui/material/DialogContent';

const rootCss = ({ breakpoints, palette, spacing }) => ({
  [`& .${dialogClasses.paperWidthSm}`]: {
    maxWidth: 680,
    backgroundColor: palette.background.default,
  },
  [`& .${dialogContentClasses.root}`]: {
    padding: 0,
    '&:first-of-type': { paddingTop: 0 },
    '& > div': {
      padding: spacing(3, 8),
      '&:first-of-type': { paddingTop: spacing(5) },
    },
  },
  [`& .${dialogActionsClasses.root}`]: {
    justifyContent: 'center',
    padding: spacing(3),
    backgroundColor: palette.background.paper,
    '& > button, & > a': { padding: '3px 26px' },
  },
  [breakpoints.only('xs')]: {
    [`& .${dialogContentClasses.root} > div`]: {
      paddingLeft: spacing(2),
      paddingRight: spacing(2),
    },
  },
});

export default function OnlineCoursePurchaseForm(props) {
  const {
    activeStep,
    course,
    currentUser,
    onChangeStep,
    onClose,
    onlineCourse,
    onlineCourseUser,
    onPurchase,
    onShowTerms,
    open,
    packageType,
    paypalLoaded,
    purchasing,
  } = props;

  const subscription = useCurrentUserSubscription();
  const classRepDiscountPercent = currentUser.has_class_rep_discount && CLASS_REP_GB_DISCOUNT_PERCENT;
  const subscriptionDiscountPercent = !classRepDiscountPercent && getSubscriptionProductDiscountPercent(subscription);

  const [discountCodeVal, setDiscountCode] = useState(currentUser.pending_discount_codes?.[0]);
  const discountCode = !subscriptionDiscountPercent && !classRepDiscountPercent && discountCodeVal;

  const paymentDetails = getPurchasePaymentDetails(
    onlineCourse,
    currentUser,
    packageType,
    discountCode,
    subscriptionDiscountPercent,
    classRepDiscountPercent,
    onlineCourseUser?.package_type,
  );

  const submitPurchase = useCallback(
    paymentFields => {
      const payments_attributes = [paymentFields];
      onPurchase(
        {
          payments_attributes,
          discount_code_id: discountCode?.id,
          expected_total: paymentDetails.total,
        },
        {
          discountCode: discountCode?.code,
          paymentDetails,
        },
      );
    },
    [onPurchase, discountCode, paymentDetails],
  );

  const header = (
    <OnlineCoursePurchaseFormHeader
      activeStep={activeStep}
      fullyDiscounted={!paymentDetails.total}
      onChangeStep={onChangeStep}
      onClose={onClose}
      purchasing={purchasing}
    />
  );

  return (
    <DialogResponsive css={rootCss} fullWidth open={open}>
      {renderContent()}
    </DialogResponsive>
  );

  function renderContent() {
    const commonProps = { course, header, onlineCourse };

    if (activeStep === 1) {
      return (
        <OnlineCoursePurchaseFormPayment
          {...commonProps}
          onShowTerms={onShowTerms}
          onSubmitPurchase={submitPurchase}
          paymentDetails={paymentDetails}
          paypalLoaded={paypalLoaded}
          purchasing={purchasing}
        />
      );
    }
    if (activeStep === 2) {
      return <OnlineCoursePurchaseFormConfirmation {...commonProps} currentUser={currentUser} onClose={onClose} />;
    }
    return (
      <OnlineCoursePurchaseFormInformation
        {...commonProps}
        disableDiscountCode={subscriptionDiscountPercent || classRepDiscountPercent}
        discountCode={discountCode}
        onChangeStep={onChangeStep}
        onSubmitPurchase={submitPurchase}
        onUpdateDiscountCode={setDiscountCode}
        paymentDetails={paymentDetails}
        purchasing={purchasing}
      />
    );
  }
}

function getPurchasePaymentDetails(
  onlineCourse,
  currentUser,
  packageType,
  discountCode,
  subDiscountPercent,
  classRepDiscountPercent,
  upgradePackageType,
) {
  let [subtotal, regularPrice] = getPackagePricing(onlineCourse, packageType);
  const lineItems = [];

  if (upgradePackageType && upgradePackageType !== 'none') {
    let [upgradeSubtotal] = getPackagePricing(onlineCourse, upgradePackageType);
    subtotal = Math.max(0, subtotal - upgradeSubtotal);

    if (upgradeSubtotal)
      lineItems.push({
        label: `${ONLINE_COURSE_PACKAGE_TYPES[upgradePackageType].name} Purchase`,
        amount: -upgradeSubtotal,
      });
  }

  lineItems.push({ label: 'Subtotal', amount: regularPrice || subtotal });

  const earlyBirdDiscount = regularPrice && regularPrice - subtotal;
  if (earlyBirdDiscount) {
    lineItems.push({ label: 'Early Bird Discount', amount: -earlyBirdDiscount });
  }

  let subDiscount, classRepDiscount, discountCodeDiscount;
  if (classRepDiscountPercent) {
    classRepDiscount = (subtotal * classRepDiscountPercent) / 100;
    subtotal -= classRepDiscount;
    lineItems.push({ label: `Class Rep ${classRepDiscountPercent}% Discount`, amount: -classRepDiscount });
  } else if (subDiscountPercent) {
    subDiscount = (subtotal * subDiscountPercent) / 100;
    subtotal -= subDiscount;
    lineItems.push({
      label: 'Wizeprep Plus Member',
      caption: `Additional ${subDiscountPercent}% Discount`,
      amount: -subDiscount,
    });
  } else {
    discountCodeDiscount = calculateDiscount(discountCode, subtotal);
    if (discountCodeDiscount) {
      subtotal -= discountCodeDiscount;
      lineItems.push({
        label: getDiscountCodeCheckoutLabel(discountCode),
        amount: -discountCodeDiscount,
      });
    }
  }
  const referralDiscount = currentUser.is_referred ? Math.min(REFERRAL_DISCOUNT_AMOUNT, subtotal) : 0;
  if (referralDiscount) {
    subtotal -= referralDiscount;
    lineItems.push({
      label: 'Refer a Friend Discount',
      amount: -referralDiscount,
    });
  }

  const creditedAmount = Math.min(subtotal, currentUser.course_credits || 0);
  if (creditedAmount) {
    subtotal -= creditedAmount;
    lineItems.push({
      label: 'Course Credits',
      amount: -creditedAmount,
    });
  }

  const salesTax = onlineCourse.sales_tax || 0;
  const taxAmount = Math.round(subtotal * salesTax) / 100;
  const isDiscounted = !!(
    earlyBirdDiscount ||
    subDiscount ||
    classRepDiscount ||
    discountCodeDiscount ||
    referralDiscount ||
    creditedAmount
  );

  if (taxAmount) {
    if (isDiscounted) {
      lineItems.push({
        label: 'Discounted Subtotal',
        amount: subtotal,
        highlight: true,
      });
    }
    lineItems.push({
      label: `Sales Tax (${salesTax}%)`,
      amount: taxAmount,
    });
  }
  const total = Math.round((subtotal + taxAmount) * 100) / 100;
  lineItems.push({
    label: 'Total',
    amount: total,
  });
  return {
    lineItems,
    total,
    tax: taxAmount,
    currency: onlineCourse.currency,
  };
}
